import { CircularProgress, Grid2, Typography, Box, Stack } from '@mui/material';
import { withStyles } from '@mui/styles';
import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import useLocalStorageState from '../../utils/useLocalStorageState';
import useProcessedTimeseetsFilters, {
    formatProcessedTimesheetFiltersForApi,
} from '../hooks/ProcessedTimesheets/useFilters';
import useGetProcessedTimesheets from '../hooks/useGetProcessedTimesheets';
import { COLORS } from '../../utils/Application_Constants';
import PageContainer from '../Components/PageContainer';
import ListViewTable from '../../Shared/Components/ListViewTable/ListViewTable';
import ListViewTableHeaderRow from '../../Shared/Components/ListViewTable/ListViewTableHeaderRow';
import ListViewTableBodyRow from '../../Shared/Components/ListViewTable/ListViewTableBodyRow';
import ListViewTableBodyCell from '../../Shared/Components/ListViewTable/ListViewTableBodyCell';
import FilterDrawer from '../../Shared/Components/FilterDrawer/FilterDrawer';
import { formatUtcDate } from '../../utils/formatDate';
import SearchInputWithDropdown from '../../Shared/Components/SearchInputWithDropdown';
import {
    searchOptions,
    returnProcessedTimesheetsColumns,
} from '../Configurations/processedTimesheetsConfig';
import BodyCell from '../Components/ProcessedTimesheets/Table/BodyCell';
import PayrollDateSortToggle from '../Components/ProcessedTimesheets/PayrollDateSortToggle';
import useThrottle from '../../utils/useThrottle';
import { pluralize } from '../../utils/helpers';

const DEFAULT_PAYROLL_DATE_ORDER = 'desc';

const styles = () => ({
    header: {
        fontWeight: '700',
        margin: 0,
        marginBottom: '20px',
    },
    eyebrowText: {
        color: COLORS.LT_MIDNIGHT,
        fontSize: '12px',
        marginBottom: '10px',
    },
    description: {
        color: COLORS.LT_MIDNIGHT,
    },
    contentContainer: {
        marginTop: '20px',
    },
    filtersWrapper: {
        paddingTop: '0.5em',
    },
    listViewWrapper: {
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '30px 10px',
        maxHeight: '70vh',
        overflowY: 'auto',
        overflowX: 'hidden', // only vertical scroll here; horizontal is on inner wrapper
    },
    // Inner wrapper: horizontal scroll only, scrollbar hidden (custom bar at bottom is used)
    listViewHorizontalScroll: {
        overflowX: 'auto',
        overflowY: 'visible',
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE and Edge
        '&::-webkit-scrollbar': {
            display: 'none', // Chrome, Safari, Opera
        },
    },
    resultsDescription: {
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '24px',
        marginTop: '16px',
        marginBottom: '20px',
        color: COLORS.LT_MIDNIGHT,
    },
});

function ProcessedTimesheetsLayout({ classes, title, pageType = 'processedTimesheets' }) {
    const listViewWrapperRef = useRef(null);
    const horizontalScrollRef = useRef(null);
    const scrollbarRef = useRef(null);
    const [scrollWidth, setScrollWidth] = useState('100%');
    const { state: sortBy, setValue: setSortBy } = useLocalStorageState(`${pageType}SortField`);
    const { state: sortDirection, setValue: setSortDirection } = useLocalStorageState(
        `${pageType}SortDirection`,
        'asc'
    );
    const { state: storedPayrollDateOrder, setValue: setPayrollDateOrder } = useLocalStorageState(
        `${pageType}PayrollDateOrder`,
        DEFAULT_PAYROLL_DATE_ORDER
    );
    const payrollDateOrder = storedPayrollDateOrder === 'asc' ? 'asc' : DEFAULT_PAYROLL_DATE_ORDER;
    const { state: searchData, setValue: setSearch } = useLocalStorageState(`${pageType}Search`, {
        searchWord: '',
        searchColumns: '',
    });
    const { state: scrollPosition, setValue: setScrollPosition } = useLocalStorageState(
        `${pageType}ScrollPosition`
    );
    const resetStoredScrollPosition = () => setScrollPosition(0);
    const scrollToTop = () => {
        if (listViewWrapperRef.current) listViewWrapperRef.current.scrollTo({ top: 0 });
    };
    const {
        filters,
        handleApplyFilter,
        filtersSetup,
        isLoadingFilterConfig,
        appliedFiltersDescription,
    } = useProcessedTimeseetsFilters(pageType);
    const requestParams = useMemo(
        () => ({
            pageSize: 20,
            payrollDateOrder,
            ...formatProcessedTimesheetFiltersForApi(filters, filtersSetup),
            ...(sortBy ? { orderBy: sortBy, dir: sortDirection } : {}),
            ...(searchData?.searchWord ? searchData : {}),
        }),
        [sortBy, sortDirection, filters, filtersSetup, searchData, payrollDateOrder]
    );

    const {
        data: timesheetsPages,
        isLoading,
        isFetching,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
    } = useGetProcessedTimesheets(requestParams);

    const groupedTimesheets = useMemo(() => {
        if (!timesheetsPages?.pages) return [];
        const tsGroupedByPayrollDate = timesheetsPages?.pages?.reduce((acc, page) => {
            page.data?.forEach((payroll) => {
                const formattedPayrollDate = formatUtcDate(payroll.payrollDate);
                if (acc[formattedPayrollDate] && acc[formattedPayrollDate].processedTimesheets) {
                    acc[formattedPayrollDate].processedTimesheets = acc[
                        formattedPayrollDate
                    ].processedTimesheets.concat(payroll?.processedTimesheets || []);
                } else {
                    acc[formattedPayrollDate] = {
                        ...payroll,
                        payrollDate: formattedPayrollDate,
                    };
                }
            });
            return acc;
        }, {});
        return Object.values(tsGroupedByPayrollDate);
    }, [timesheetsPages]);

    const throttledFetchNextPage = useThrottle(fetchNextPage, 1000);

    const handleScroll = useCallback(() => {
        if (isFetchingNextPage || !hasNextPage) return;
        const { scrollTop, scrollHeight, clientHeight } = listViewWrapperRef.current;
        setScrollPosition(scrollTop);
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
        if (isAtBottom) throttledFetchNextPage();
    }, [throttledFetchNextPage, isFetchingNextPage, hasNextPage]);

    const throttledScrollHandler = useThrottle(handleScroll, 200);

    useEffect(() => {
        if (isLoading) return; // add scroll listener only after first load
        listViewWrapperRef.current?.addEventListener('scroll', throttledScrollHandler);
        return () =>
            listViewWrapperRef.current?.removeEventListener('scroll', throttledScrollHandler);
    }, [throttledScrollHandler, isLoading]);

    useEffect(() => {
        // reset scroll position when loading (first load or filters changed)
        if (isLoading) {
            resetStoredScrollPosition();
        } else if (!isLoading && scrollPosition && listViewWrapperRef.current) {
            listViewWrapperRef.current.scrollTo({ top: scrollPosition });
        }
    }, [isLoading]);

    // Sync horizontal scrollbar with inner scroll container
    useEffect(() => {
        const horizontalScroll = horizontalScrollRef.current;
        const scrollbar = scrollbarRef.current;

        if (!horizontalScroll || !scrollbar) return;

        const handleHorizontalScroll = () => {
            scrollbar.scrollLeft = horizontalScroll.scrollLeft;
        };

        const handleScrollbarScroll = () => {
            horizontalScroll.scrollLeft = scrollbar.scrollLeft;
        };

        const handleResize = () => {
            if (horizontalScroll) {
                setScrollWidth(`${horizontalScroll.scrollWidth}px`);
            }
        };

        horizontalScroll.addEventListener('scroll', handleHorizontalScroll);
        scrollbar.addEventListener('scroll', handleScrollbarScroll);
        window.addEventListener('resize', handleResize);

        if (horizontalScrollRef.current) {
            setScrollWidth(`${horizontalScrollRef.current.scrollWidth}px`);
        }

        return () => {
            horizontalScroll.removeEventListener('scroll', handleHorizontalScroll);
            scrollbar.removeEventListener('scroll', handleScrollbarScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [groupedTimesheets]);

    const handleSortChange = useCallback((key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
        scrollToTop();
    }, []);

    const handlePayrollDateOrderChange = useCallback((nextValue) => {
        setPayrollDateOrder(nextValue);
        scrollToTop();
    }, []);

    const totalCount = timesheetsPages?.pages?.[0]?.totalCount || 0;
    const columns = useMemo(() => returnProcessedTimesheetsColumns(pageType), [pageType]);

    return (
        <PageContainer>
            <Grid2 container direction="column" spacing={6}>
                <Grid2 size={12}>
                    <Typography variant="subtitle2" component="p" className={classes.eyebrowText}>
                        Processed Timesheets
                    </Typography>
                    <Typography variant="h3" component="h3" className={classes.header}>
                        {title}
                    </Typography>
                    <Grid2 size={4}>
                        <Typography variant="p" component="p" className={classes.description}>
                            To prioritize your work, filters have been pre-applied. We recommend
                            filtering by division and statuses.
                        </Typography>
                    </Grid2>
                </Grid2>
            </Grid2>
            <div className={classes.contentContainer}>
                <Stack
                    direction="row"
                    gap={2}
                    alignItems="center"
                    className={classes.filtersWrapper}
                >
                    <FilterDrawer
                        isLoading={isLoadingFilterConfig || isLoading}
                        config={filtersSetup}
                        onChange={handleApplyFilter}
                        storedValues={filters}
                    />
                    <Stack direction="row" gap={1} sx={{ alignItems: 'center', mr: 'auto' }}>
                        <Typography variant="body1">Payroll Date sort</Typography>
                        <PayrollDateSortToggle
                            value={payrollDateOrder}
                            onChange={handlePayrollDateOrderChange}
                        />
                    </Stack>

                    <SearchInputWithDropdown
                        isLoading={isLoading}
                        options={searchOptions}
                        initialSearch={searchData?.searchWord}
                        initialSearchKey={searchData?.searchColumns}
                        onSubmit={({ search, searchKey }) =>
                            setSearch({ searchWord: search, searchColumns: searchKey })
                        }
                    />
                </Stack>

                <Typography className={classes.resultsDescription}>
                    {`${totalCount} ${pluralize(totalCount, 'item')}`} • {appliedFiltersDescription}
                </Typography>

                {!isLoading && groupedTimesheets.length > 0 ? (
                    <Box sx={{ position: 'relative' }}>
                        <div
                            className={classes.listViewWrapper}
                            ref={listViewWrapperRef}
                            data-testid="list-view-wrapper"
                        >
                            <div
                                className={classes.listViewHorizontalScroll}
                                ref={horizontalScrollRef}
                            >
                                {groupedTimesheets.map((payroll) => (
                                    <ListViewTable
                                        key={payroll.payrollDate}
                                        title={`Payroll Date ${payroll.payrollDate} - (${payroll.processedTimesheets.length})`}
                                    >
                                        <ListViewTableHeaderRow
                                            columns={columns}
                                            onSortChange={handleSortChange}
                                            sortBy={sortBy}
                                            sortDirection={sortDirection}
                                            shouldCleanSort
                                        />
                                        {payroll.processedTimesheets != null &&
                                            payroll.processedTimesheets.length > 0 && (
                                                <>
                                                    {payroll.processedTimesheets.map(
                                                        (pts, index) => (
                                                            <ListViewTableBodyRow
                                                                key={`payrollDate:${payroll.payrollDate}|ts:${pts.id}`}
                                                                index={index}
                                                            >
                                                                {columns.map((column) => (
                                                                    <ListViewTableBodyCell
                                                                        key={`ts:${pts.id}-${column.key}`}
                                                                        size={column.size}
                                                                        sx={column.sx}
                                                                    >
                                                                        <BodyCell
                                                                            pts={pts}
                                                                            column={column}
                                                                        />
                                                                    </ListViewTableBodyCell>
                                                                ))}
                                                            </ListViewTableBodyRow>
                                                        )
                                                    )}
                                                </>
                                            )}
                                    </ListViewTable>
                                ))}
                                {!hasNextPage && (
                                    <Typography>You&apos;re all caught up!</Typography>
                                )}
                                {isFetchingNextPage && (
                                    <CircularProgress size={50} sx={{ my: 5 }} />
                                )}
                            </div>
                        </div>

                        {/* Static horizontal scrollbar at the bottom */}
                        <Box
                            ref={scrollbarRef}
                            sx={{
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                position: 'sticky',
                                bottom: 0,
                                left: 0,
                                zIndex: 10,
                                backgroundColor: 'white',
                                borderTop: '1px solid #e0e0e0',
                                paddingTop: '2px',
                                paddingBottom: '2px',
                                // Force scrollbar to always be visible
                                '&::-webkit-scrollbar': {
                                    height: '10px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#888',
                                    borderRadius: '6px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: '#555',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    height: '1px',
                                    width: scrollWidth,
                                }}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box className={classes.listViewWrapper}>
                        <Typography variant="body1" component="p" p={2}>
                            {isLoading || isFetching ? <CircularProgress /> : 'No results found'}
                        </Typography>
                    </Box>
                )}
            </div>
        </PageContainer>
    );
}

export default withStyles(styles)(ProcessedTimesheetsLayout);
